import { z } from "zod";

import { appearanceIdSchema } from "./character";

export const participantRoleSchema = z.enum([
  "present",
  "pov",
  "mentioned",
  "heard-only",
]);

export const participantSchema = z.object({
  characterId: z.string().min(1),
  role: participantRoleSchema,
  /** Set only when this beat uses one of the character's significant looks. */
  appearanceId: appearanceIdSchema.optional(),
  /** There, but the reader cannot know yet, such as someone hiding as a flea. Spoilers only. */
  secret: z.boolean().optional(),
});

/** Local story time, `YYYY-MM-DDTHH:mm:ss`, with no timezone. */
const approxStartSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/);

export const narrativeTimeSchema = z.object({
  mode: z.enum(["absolute", "relative", "montage", "unknown"]),
  label: z.string().min(1),
  approxStart: approxStartSchema.optional(),
  /** How long this scene's action lasts, in minutes. */
  durationMinutes: z.number().positive().optional(),
  /** How much of approxStart the book supports: the player shows no more than this. */
  precision: z.enum(["moment", "hour", "night", "day", "month", "year"]).optional(),
  confidence: z.enum(["high", "medium", "low"]).optional(),
});

/** Set by a later chapter's draft: what that chapter reveals about an earlier scene. */
export const spoilerNoteSchema = z.object({
  sceneId: z.string().min(1),
  /** The person it is about, when it is about one person. */
  characterId: z.string().min(1).optional(),
  /** The name they turn out to have, when the scene did not know it. */
  revealedName: z.string().min(1).optional(),
  details: z.string().min(1),
});

/** A note on the scene it is about, with the chapter that reveals it. Set at compile time. */
export const sceneSpoilerSchema = spoilerNoteSchema.omit({ sceneId: true }).extend({
  revealedInChapterId: z.string().min(1),
  revealedInTitle: z.string().min(1),
});

export const sceneSchema = z.object({
  id: z.string().min(1),
  chapterId: z.string().min(1),
  title: z.string().min(1),
  summary: z.string().min(1),
  /** A flashback as it happens, for story order, where nobody is remembering it. */
  storySummary: z.string().min(1).optional(),
  bookOrder: z.number().int().positive(),
  inUniverseOrder: z.number().int().positive(),
  inUniverseSubOrder: z.number().int().nonnegative().default(0),
  location: z.object({
    label: z.string().min(1),
    scope: z.string().optional(),
  }),
  /** Most specific place. Parent links live in the chapter places list. */
  placeId: z.string().min(1).optional(),
  narrativeTime: narrativeTimeSchema.optional(),
  participants: z.array(participantSchema).min(1),
  spoilers: z.array(sceneSpoilerSchema).optional(),
  /** People inside a vehicle here, shown small on its marker rather than on the rail. */
  aboard: z.array(z.object({
    characterId: z.string().min(1),
    vehicleId: z.string().min(1),
  })).optional(),
  /** Who goes to stand beside a vehicle here, then returns to their seat. */
  visits: z.array(z.object({
    characterId: z.string().min(1),
    vehicleId: z.string().min(1),
  })).optional(),
  /** Who takes an animal's DNA here. Their markers touch while it happens. */
  acquires: z.array(z.object({
    characterId: z.string().min(1),
    fromCharacterId: z.string().min(1),
    /** What the marker says, such as "Acquiring the tiger". */
    label: z.string().min(1),
  })).optional(),
  /** Who dies in this scene, shown on their marker. */
  deaths: z.array(z.object({
    characterId: z.string().min(1),
    /** What the marker says, such as "Killed by Visser Three". */
    label: z.string().min(1),
  })).optional(),
  /** People who go after this scene, rather than waiting here until they next appear. */
  leaves: z.array(z.string().min(1)).optional(),
  /** Groups off the page during this scene, who still follow their own rail. */
  elsewhere: z.array(z.object({
    placeId: z.string().min(1),
    characterIds: z.array(z.string().min(1)).min(1),
    /** Only as told, such as whoever is telling an old story. Story order leaves them out. */
    asToldOnly: z.boolean().optional(),
    /** Set at compile time from the book's fixed looks: character id to look id. */
    looks: z.record(z.string(), z.string().min(1)).optional(),
  })).optional(),
  /** Set at compile time from the book's key items. */
  keyItems: z.array(z.object({
    itemId: z.string().min(1),
    name: z.string().min(1),
    characterIds: z.array(z.string().min(1)),
    fate: z.string().min(1).optional(),
    secret: z.boolean().optional(),
    /** Its power is at work in this scene. */
    glow: z.boolean().optional(),
    /** Who is wearing it here and so cannot be seen. */
    invisibleCharacterIds: z.array(z.string().min(1)).optional(),
  })).optional(),
});

export const scenesFileSchema = z.object({
  scenes: z.array(sceneSchema).min(1),
});

export type SpoilerNote = z.infer<typeof spoilerNoteSchema>;
export type SceneSpoiler = z.infer<typeof sceneSpoilerSchema>;
export type NarrativeTime = z.infer<typeof narrativeTimeSchema>;
export type Scene = z.infer<typeof sceneSchema>;
export type ScenesFile = z.infer<typeof scenesFileSchema>;
export type ParticipantRole = z.infer<typeof participantRoleSchema>;
